import type { Dispatch, SerializedError, UnknownAction } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { type MouseEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import {
  extractErrorsFromValidation,
  loadFixingOrderXLSX,
  loadStopLossOrderXLSX,
} from '@/store/bulkOrder/helpers/utils.ts';
import {
  fixingOrderInvalid,
  fixingOrderPropertiesChanged,
  fixingOrdersLoaded,
  fixingOrderStatusChanged,
  stopLossOrderInvalid,
  stopLossOrderPropertiesChanged,
  stopLossOrdersLoaded,
  stopLossOrderStatusChanged,
} from '@/store/bulkOrder/slice.ts';
import {
  BulkOrderStatus,
  type FixingBulkOrder,
  type FixingOrderError,
  type FixingOrderRow,
  type StopLossBulkOrder,
  type StopLossOrderError,
  type StopLossOrderRow,
} from '@/store/bulkOrder/state.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { useValidateFixingMutation } from '@/store/orders/fixing/api/index.ts';
import type { ValidateFixingOrderRequest } from '@/store/orders/fixing/state.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import { useValidateStopLossMutation } from '@/store/orders/stopLoss/api/index.ts';
import type { ValidateStopLossOrderRequest } from '@/store/orders/stopLoss/state.ts';
import { selectOrderEmails } from '@/store/userPreferences/selectors.ts';

type IsImportingWithNotRightType = 'fixing' | 'stopLoss' | undefined;

//  ██████╗ ██████╗ ███╗   ███╗██████╗  ██████╗ ███╗   ██╗███████╗███╗   ██╗████████╗
// ██╔════╝██╔═══██╗████╗ ████║██╔══██╗██╔═══██╗████╗  ██║██╔════╝████╗  ██║╚══██╔══╝
// ██║     ██║   ██║██╔████╔██║██████╔╝██║   ██║██╔██╗ ██║█████╗  ██╔██╗ ██║   ██║
// ██║     ██║   ██║██║╚██╔╝██║██╔═══╝ ██║   ██║██║╚██╗██║██╔══╝  ██║╚██╗██║   ██║
// ╚██████╗╚██████╔╝██║ ╚═╝ ██║██║     ╚██████╔╝██║ ╚████║███████╗██║ ╚████║   ██║
//  ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═══╝   ╚═╝

export const ImportInput: React.FunctionComponent<{
  setStep: (step: number) => void;
  setHasErrorImporting: (hasErrorImporting: boolean) => void;
  setIsImportingWithNoRight: (type: IsImportingWithNotRightType) => void;
  tabId: string;
}> = ({ setStep, setHasErrorImporting, setIsImportingWithNoRight, tabId }) => {
  const dispatch = useDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const emails = useAppSelector(selectOrderEmails);

  const [validateFixingOrder] = useValidateFixingMutation();
  const [validateStopLossOrder] = useValidateStopLossMutation();
  const { data: currentUser } = useCurrentUserQuery();

  const canImportStopLossXLSXFile = currentUser?.productsAccess.stopLoss;
  const canImportFixingXLSXFile = currentUser?.productsAccess.fixing;

  const [dragging, setDragging] = useState(false);
  const onUndrag = useCallback(() => setDragging(false), []);

  const onFileChange = useCallback(
    async (file: File | undefined) => {
      if (isNotDefined(file)) {
        return;
      }

      sgwtWebAnalytics?.trackEvent('BulkOrder', 'Import file', file.name);
      const arrayBuffer = await file.arrayBuffer();
      const processFileHandler = processFile({
        setHasErrorImporting,
        canImportStopLossXLSXFile,
        dispatch,
        setIsImportingWithNoRight,
        emails,
        validateStopLossOrder,
        canImportFixingXLSXFile,
        validateFixingOrder,
        setStep,
        tabId,
      });

      if (processFileHandler) {
        await processFileHandler({ target: { result: arrayBuffer } } as ProgressEvent<FileReader>);
      }
    },
    [
      setStep,
      setHasErrorImporting,
      setIsImportingWithNoRight,
      emails,
      validateStopLossOrder,
      validateFixingOrder,
      canImportStopLossXLSXFile,
      canImportFixingXLSXFile,
      tabId,
      sgwtWebAnalytics,
    ],
  );

  const onChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      await onFileChange(file);
    },
    [onFileChange],
  );

  const onDrag = useCallback(() => setDragging(true), []);

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      onUndrag();
      await onFileChange(e.dataTransfer.files?.item(0) ?? undefined);
    },
    [onUndrag, onFileChange],
  );

  const preventDrop = (event: Event) => event.preventDefault();

  useEffect(() => {
    document.addEventListener('dragover', preventDrop);
    document.addEventListener('drop', preventDrop);

    return () => {
      document.removeEventListener('dragover', preventDrop);
      document.removeEventListener('drop', preventDrop);
    };
  });

  const forceToOpenOSFileImporter = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className={`position-relative d-block${dragging ? ' bg-lvl2' : ''}`} onClick={forceToOpenOSFileImporter}>
      <div className="upload-squares" />
      <i className="icon icon-xl text-light mx-2 mt-3">attach_file</i>
      <div className="upload-inner px-3 pb-3 text-center">
        <h5 className="text-secondary mb-2">Add attachment</h5>
        <p className="text-secondary m-0 line-height-1">
          <FormattedMessage id="bulkOrder.modal.import.dragOrSelect" values={selectValues} />
        </p>
      </div>
      <label
        className="position-absolute h-100 w-100 cursor-pointer top-0 start-0"
        htmlFor="file"
        onDragEnter={onDrag}
        onDragLeave={onUndrag}
        onDragEnd={onUndrag}
        onDrop={onDrop}
      >
        {' '}
      </label>
      <input
        className="invisible"
        type="file"
        id="file"
        name="file"
        accept=".xlsx"
        data-e2e="select-file"
        onChange={onChange}
      />
      <div className="upload-squares" />
    </div>
  );
};

const selectValues = {
  select: (select: ReactNode) => (
    <label htmlFor="bulk_file" className="text-info">
      {select}
    </label>
  ),
};

interface ProcessFileOptions {
  setHasErrorImporting: (hasErrorImporting: boolean) => void;
  canImportStopLossXLSXFile: boolean | undefined;
  dispatch: Dispatch<UnknownAction>;
  setIsImportingWithNoRight: (type: IsImportingWithNotRightType) => void;
  emails: string[];
  validateStopLossOrder: (body: ValidateStopLossOrderRequest) => Promise<{
    data?: ValidateOrderResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  canImportFixingXLSXFile: boolean | undefined;
  validateFixingOrder: (body: ValidateFixingOrderRequest) => Promise<{
    data?: ValidateOrderResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  setStep: (step: number) => void;
  tabId: string;
}

function processFile(options: ProcessFileOptions): ((event: ProgressEvent<FileReader>) => Promise<void>) | null {
  const {
    setHasErrorImporting,
    canImportStopLossXLSXFile,
    dispatch,
    setIsImportingWithNoRight,
    emails,
    validateStopLossOrder,
    canImportFixingXLSXFile,
    validateFixingOrder,
    setStep,
    tabId,
  } = options;
  return async (e) => {
    const arrayBuffer = e.target?.result;
    if (!(arrayBuffer instanceof ArrayBuffer)) {
      return;
    }

    const data = new Uint8Array(arrayBuffer);
    const binaryStr = Array.from(data)
      .map((byte) => String.fromCodePoint(byte))
      .join('');

    const workbook = XLSX.read(binaryStr, { type: 'binary' });

    const sheetIncludesStopLoss = workbook.SheetNames.includes('StopLoss');
    const sheetIncludesFixing = workbook.SheetNames.includes('Fixing');

    if (!sheetIncludesStopLoss && !sheetIncludesFixing) {
      setHasErrorImporting(true);
      return;
    }

    importStopLossOrders({
      sheetIncludesStopLoss,
      canImportStopLossXLSXFile: canImportStopLossXLSXFile ?? false,
      workbook,
      setHasErrorImporting,
      dispatch,
      setIsImportingWithNoRight,
      emails,
      validateStopLossOrder,
      tabId,
    });

    importFixingOrders({
      sheetIncludesFixing,
      canImportFixingXLSXFile: canImportFixingXLSXFile ?? false,
      workbook,
      setHasErrorImporting,
      dispatch,
      setIsImportingWithNoRight,
      emails,
      validateFixingOrder,
      tabId,
    });

    setStep(2);
  };
}

// ██╗███╗   ███╗██████╗  ██████╗ ██████╗ ████████╗    ███████╗████████╗ ██████╗ ██████╗     ██╗      ██████╗ ███████╗███████╗
// ██║████╗ ████║██╔══██╗██╔═══██╗██╔══██╗╚══██╔══╝    ██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗    ██║     ██╔═══██╗██╔════╝██╔════╝
// ██║██╔████╔██║██████╔╝██║   ██║██████╔╝   ██║       ███████╗   ██║   ██║   ██║██████╔╝    ██║     ██║   ██║███████╗███████╗
// ██║██║╚██╔╝██║██╔═══╝ ██║   ██║██╔══██╗   ██║       ╚════██║   ██║   ██║   ██║██╔═══╝     ██║     ██║   ██║╚════██║╚════██║
// ██║██║ ╚═╝ ██║██║     ╚██████╔╝██║  ██║   ██║       ███████║   ██║   ╚██████╔╝██║         ███████╗╚██████╔╝███████║███████║
// ╚═╝╚═╝     ╚═╝╚═╝      ╚═════╝ ╚═╝  ╚═╝   ╚═╝       ╚══════╝   ╚═╝    ╚═════╝ ╚═╝         ╚══════╝ ╚═════╝ ╚══════╝╚══════╝

interface ImportStopLossOrdersOptions {
  sheetIncludesStopLoss: boolean;
  canImportStopLossXLSXFile: boolean;
  workbook: XLSX.WorkBook;
  setHasErrorImporting: (hasErrorImporting: boolean) => void;
  dispatch: Dispatch<UnknownAction>;
  setIsImportingWithNoRight: (type: 'fixing' | 'stopLoss' | undefined) => void;
  emails: string[];
  validateStopLossOrder: (body: ValidateStopLossOrderRequest) => Promise<{
    data?: ValidateOrderResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  tabId: string;
}

async function importStopLossOrders(options: ImportStopLossOrdersOptions) {
  const {
    sheetIncludesStopLoss,
    canImportStopLossXLSXFile,
    workbook,
    setHasErrorImporting,
    dispatch,
    setIsImportingWithNoRight,
    emails,
    validateStopLossOrder,
    tabId,
  } = options;

  if (sheetIncludesStopLoss && canImportStopLossXLSXFile) {
    const sheet1 = workbook.Sheets.StopLoss;
    const stopLossData = XLSX.utils.sheet_to_json(sheet1 as XLSX.WorkSheet);

    if (stopLossData.length > 0) {
      setHasErrorImporting(false);
      const allStopLossOrders = loadStopLossOrderXLSX(stopLossData as StopLossOrderRow[], [...emails]);
      dispatch(stopLossOrdersLoaded({ stopLosses: allStopLossOrders, tabId }));

      await handleStopLossOrdersImport({
        dispatch,
        allStopLossOrders,
        validateStopLossOrder,
        tabId,
      });
    }
  } else if (sheetIncludesStopLoss) {
    setIsImportingWithNoRight('stopLoss');
  }
}

const handleStopLossOrdersImport = async ({
  allStopLossOrders,
  dispatch,
  validateStopLossOrder,
  tabId,
}: {
  allStopLossOrders: StopLossBulkOrder[];
  dispatch: Dispatch<UnknownAction>;
  validateStopLossOrder: (body: ValidateStopLossOrderRequest) => Promise<{
    data?: ValidateOrderResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  tabId: string;
}) => {
  await Promise.all(
    allStopLossOrders.map(async (order) => {
      dispatch(
        stopLossOrderStatusChanged({
          orderId: order.properties.id,
          status: BulkOrderStatus.VALIDATING,
          tabId,
        }),
      );
      const [currency1, currency2] = order.properties.currencyPair.split('/');

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error('Invalid currency pair:', order.properties.currencyPair);
        return;
      }

      const { data: stopLossValidation, error } = await validateStopLossOrder({
        tileId: order.properties.id,
        clientId: Number(order.properties.bdrId),
        way: order.properties.way === 'Ask' ? 'buy' : 'sell',
        notional: order.properties.notional,
        notionalCurrencyIndex: order.properties.currencyIndex,
        currency1,
        currency2,
        limitPrice: order.properties.limitPrice,
        expiry: order.properties.expiry,
        trigger: order.properties.trigger,
        notificationEmails: { emails: order.properties.notificationEmails },
        clockOffset: order.properties.clockOffset,
      });

      if (isDefined(error)) {
        console.error('error during the order validation', error);
        dispatch(
          stopLossOrderInvalid({
            orderId: order.properties.id,
            errors: extractErrorsFromValidation(
              (error as ValidateOrderResponse).validationResponseDetailContent,
            ) as StopLossOrderError[],
            tabId,
          }),
        );
        return;
      }

      if (isNotDefined(stopLossValidation)) {
        return;
      }

      const currentTime = Date.now();
      const clockOffset =
        stopLossValidation.backendTime !== null && stopLossValidation.backendTime !== 0
          ? stopLossValidation.backendTime - currentTime
          : undefined;
      dispatch(
        stopLossOrderPropertiesChanged({
          orderId: order.properties.id,
          properties: { clockOffset },
          tabId,
        }),
      );

      stopLossValidation.isReadyToSubmit
        ? dispatch(
            stopLossOrderStatusChanged({
              orderId: order.properties.id,
              status: BulkOrderStatus.VALID,
              tabId,
            }),
          )
        : dispatch(
            stopLossOrderInvalid({
              orderId: order.properties.id,
              errors: extractErrorsFromValidation(
                stopLossValidation.validationResponseDetailContent,
              ) as StopLossOrderError[],
              tabId,
            }),
          );
    }),
  );
};

// ██╗███╗   ███╗██████╗  ██████╗ ██████╗ ████████╗    ███████╗██╗██╗  ██╗██╗███╗   ██╗ ██████╗
// ██║████╗ ████║██╔══██╗██╔═══██╗██╔══██╗╚══██╔══╝    ██╔════╝██║╚██╗██╔╝██║████╗  ██║██╔════╝
// ██║██╔████╔██║██████╔╝██║   ██║██████╔╝   ██║       █████╗  ██║ ╚███╔╝ ██║██╔██╗ ██║██║  ███╗
// ██║██║╚██╔╝██║██╔═══╝ ██║   ██║██╔══██╗   ██║       ██╔══╝  ██║ ██╔██╗ ██║██║╚██╗██║██║   ██║
// ██║██║ ╚═╝ ██║██║     ╚██████╔╝██║  ██║   ██║       ██║     ██║██╔╝ ██╗██║██║ ╚████║╚██████╔╝
// ╚═╝╚═╝     ╚═╝╚═╝      ╚═════╝ ╚═╝  ╚═╝   ╚═╝       ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝ ╚═════╝

interface ImportFixingOrdersOptions {
  sheetIncludesFixing: boolean;
  canImportFixingXLSXFile: boolean;
  workbook: XLSX.WorkBook;
  setHasErrorImporting: (hasErrorImporting: boolean) => void;
  dispatch: Dispatch<UnknownAction>;
  setIsImportingWithNoRight: (type: 'fixing' | 'stopLoss' | undefined) => void;
  emails: string[];
  validateFixingOrder: (body: ValidateFixingOrderRequest) => Promise<{
    data?: ValidateOrderResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  tabId: string;
}

async function importFixingOrders(options: ImportFixingOrdersOptions) {
  const {
    sheetIncludesFixing,
    canImportFixingXLSXFile,
    workbook,
    setHasErrorImporting,
    dispatch,
    setIsImportingWithNoRight,
    emails,
    validateFixingOrder,
    tabId,
  } = options;

  if (sheetIncludesFixing && canImportFixingXLSXFile) {
    const sheet2 = workbook.Sheets.Fixing;
    const fixingData = XLSX.utils.sheet_to_json(sheet2 as XLSX.WorkSheet);

    if (fixingData.length > 0) {
      setHasErrorImporting(false);
      const allFixingOrders = loadFixingOrderXLSX(fixingData as FixingOrderRow[], [...emails]);
      dispatch(fixingOrdersLoaded({ fixings: allFixingOrders, tabId }));

      await handleFixingOrdersImport({
        dispatch,
        allFixingOrders,
        validateFixingOrder,
        tabId,
      });
    }
  } else if (sheetIncludesFixing) {
    setIsImportingWithNoRight('fixing');
  }
}

const handleFixingOrdersImport = async ({
  allFixingOrders,
  dispatch,
  validateFixingOrder,
  tabId,
}: {
  allFixingOrders: FixingBulkOrder[];
  dispatch: Dispatch<UnknownAction>;
  validateFixingOrder: (body: ValidateFixingOrderRequest) => Promise<{
    data?: ValidateOrderResponse;
    error?: FetchBaseQueryError | SerializedError;
  }>;
  tabId: string;
}) => {
  await Promise.all(
    allFixingOrders.map(async (order) => {
      dispatch(
        fixingOrderStatusChanged({
          orderId: order.properties.id,
          status: BulkOrderStatus.VALIDATING,
          tabId,
        }),
      );

      const [currency1, currency2] = order.properties.currencyPair.split('/');

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error('Invalid currency pair:', order.properties.currencyPair);
        return;
      }

      const { data: fixingValidation, error } = await validateFixingOrder({
        tileId: order.properties.id,
        clientId: Number(order.properties.bdrId),
        way: order.properties.way === 'Ask' ? 'buy' : 'sell',
        notional: order.properties.notional,
        notionalCurrencyIndex: order.properties.currencyIndex,
        expiryDay: order.properties.deliveryDate,
        currency1,
        currency2,
        fixingReference: order.properties.fixingReference,
        fixingPlace: order.properties.fixingPlace,
        fixingTime: order.properties.fixingTime,
        fixingPriceType: order.properties.fixingPriceType,
        fixingDateUtc: order.properties.fixingDateUtc,
        commercialMargin: order.properties.commercialMargin,
        fixingMarginType: order.properties.fixingMarginType,
        notificationEmails: { emails: order.properties.notificationEmails },
        clockOffset: order.properties.clockOffset,
      });

      if (isDefined(error)) {
        console.error('error during the order validation', error);
        dispatch(
          fixingOrderInvalid({
            orderId: order.properties.id,
            errors: extractErrorsFromValidation(
              (error as ValidateOrderResponse).validationResponseDetailContent,
            ) as FixingOrderError[],
            tabId,
          }),
        );
        return;
      }

      if (isNotDefined(fixingValidation)) {
        return;
      }

      const currentTime = Date.now();
      const clockOffset =
        fixingValidation.backendTime !== null && fixingValidation.backendTime !== 0
          ? fixingValidation.backendTime - currentTime
          : undefined;
      dispatch(
        fixingOrderPropertiesChanged({
          orderId: order.properties.id,
          properties: { clockOffset },
          tabId,
        }),
      );

      if (isDefined(fixingValidation.marginInBps)) {
        dispatch(
          fixingOrderPropertiesChanged({
            orderId: order.properties.id,
            properties: { commercialMargin: fixingValidation.marginInBps },
            tabId,
          }),
        );
      }

      fixingValidation.isReadyToSubmit
        ? dispatch(
            fixingOrderStatusChanged({
              orderId: order.properties.id,
              status: BulkOrderStatus.VALID,
              tabId,
            }),
          )
        : dispatch(
            fixingOrderInvalid({
              orderId: order.properties.id,
              errors: extractErrorsFromValidation(
                fixingValidation.validationResponseDetailContent,
              ) as FixingOrderError[],
              tabId,
            }),
          );
    }),
  );
};
